import { useLayoutEffect, useRef } from "react";

import mergeRefs from "merge-refs";
import { twJoin } from "tailwind-merge";

type ChatMessageListPagination = {
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  loadingSkeleton: React.ReactNode;
};

type ChatMessageListProps = {
  children: React.ReactNode;
  dataLoc?: string;
  pagination?: ChatMessageListPagination;
  // Incompatible with `pagination`, whose scroll restoration has to land in one
  // frame.
  smoothScroll?: boolean;
  ref?: React.Ref<HTMLUListElement>;
};

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  children,
  dataLoc,
  pagination,
  smoothScroll = false,
  ref,
}) => {
  const internalRef = useRef<HTMLUListElement>(null);
  const prevScrollDataRef = useRef<{ height: number; top: number } | null>(
    null,
  );

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    if (!pagination) return;
    const { hasNextPage, isFetchingNextPage, fetchNextPage } = pagination;
    const list = internalRef.current;

    if (!list) return;

    if (isFetchingNextPage && prevScrollDataRef.current?.top !== undefined) {
      // Keep tracking the scroll top when fetching next page
      // User can keep scrolling up while the next page is fetching
      prevScrollDataRef.current.top = e.currentTarget.scrollTop;
    }

    if (list.scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      prevScrollDataRef.current = {
        height: list.scrollHeight,
        top: list.scrollTop,
      };

      fetchNextPage();
    }
  };

  useLayoutEffect(() => {
    if (!pagination) return;

    const list = internalRef.current;
    const scrollData = prevScrollDataRef.current;

    if (!list || !scrollData) return;

    if (
      !pagination.isFetchingNextPage &&
      list.scrollHeight !== scrollData.height
    ) {
      list.scrollTop = scrollData.top + (list.scrollHeight - scrollData.height);

      prevScrollDataRef.current = null;
    }
  }, [children, pagination?.isFetchingNextPage]);

  return (
    <ul
      ref={mergeRefs(internalRef, ref)}
      className={twJoin(
        "decideScrollbar flex h-full flex-col gap-y-2 p-2.5",
        smoothScroll && "motion-safe:scroll-smooth",
      )}
      data-loc={dataLoc}
      onScroll={handleScroll}
    >
      {pagination?.hasNextPage && pagination.loadingSkeleton}
      {children}
    </ul>
  );
};
